import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The nine semantic type steps are `--text-*` theme entries, so Tailwind emits
 * them as `text-micro` … `text-hero`. tailwind-merge cannot tell those from
 * `text-muted-foreground` and files them under *text-colour*, which means any
 * `cn()` that carries a size step and a colour class silently drops the size —
 * the colour wins the group. Registering the nine names as font sizes restores
 * the intended behaviour and additionally lets a step override a built-in
 * (`cn('text-ui', 'text-title')` now resolves to `text-title`).
 */
const TYPE_STEPS = [
  'micro',
  'caption',
  'body',
  'ui',
  'readout',
  'title',
  'page',
  'display',
  'hero',
] as const;

/**
 * The kit's density and layout spacing keys (`h-control`, `px-cell-x`,
 * `w-sheet` …) from `@bms/common/styles/tokens.css`; registered so an
 * override such as `h-8` replaces a primitive's `h-control` instead of
 * sitting beside it. Kept in step with `packages/common/src/components/ui/cn.ts`.
 */
const SPACING_KEYS = [
  'control',
  'control-sm',
  'row',
  'cell-x',
  'cell-y',
  'form-row',
  'table-header',
  'shell-header',
  'page-header',
  'toolbar',
  'sidebar',
  'sidebar-rail',
  'sheet',
  'sheet-lg',
  'page-padding',
] as const;

const twMerge = extendTailwindMerge({
  extend: {
    theme: { spacing: [...SPACING_KEYS] },
    classGroups: { 'font-size': [{ text: [...TYPE_STEPS] }] },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
